import clsx from 'clsx'
import type React from 'react'

type AvatarProps = {
  src?: string | null
  square?: boolean
  initials?: string
  alt?: string
  className?: string
  /**
   * The hairline around the avatar. Catalyst always draws it; ours is
   * optional, because a supplied logo with a transparent background is
   * not a tile and a line around it reads as a box drawn around the mark.
   * A photo or an initials square IS a tile and keeps it.
   *
   * A prop rather than an overriding class on purpose: `outline` and
   * `outline-none` are the same kind of utility, so which wins is decided
   * by Tailwind's own ordering of its stylesheet, not by the order they
   * appear in the class attribute. Not something to leave to chance.
   */
  outline?: boolean
}

export function Avatar({
  src = null,
  square = false,
  initials,
  alt = '',
  className,
  outline = true,
  ...props
}: AvatarProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      data-slot="avatar"
      {...props}
      className={clsx(
        className,
        // Basic layout
        'inline-grid shrink-0 align-middle [--avatar-radius:var(--radius-md)] *:col-start-1 *:row-start-1',
        outline && 'outline -outline-offset-1 outline-line',
        // Initials on the avatar ground, at zero specificity (`:where`) so a
        // caller's own `bg-*`/`text-*` (a brand mark, a per-site tint) wins
        initials && !src && '[:where(&)]:bg-avatar [:where(&)]:text-fg',
        // Border radius
        square ? 'rounded-(--avatar-radius) *:rounded-(--avatar-radius)' : 'rounded-full *:rounded-full'
      )}
    >
      {initials && (
        <svg
          className="size-full fill-current p-[5%] text-[48px] font-medium uppercase select-none"
          viewBox="0 0 100 100"
          aria-hidden={alt ? undefined : 'true'}
        >
          {alt && <title>{alt}</title>}
          <text x="50%" y="50%" alignmentBaseline="middle" dominantBaseline="middle" textAnchor="middle" dy=".125em">
            {initials}
          </text>
        </svg>
      )}
      {src && <img className="size-full" src={src} alt={alt} />}
    </span>
  )
}
